import React, { useEffect, useState } from 'react'
import { useQuery } from 'react-query'

import makeStyles from '@mui/styles/makeStyles'
import { Button, Checkbox } from '@mui/material'

import { useRouter } from '@shared/hooks/use-router'
import { apiJSON } from '@shared/utils/api/api.util'

import { Aspect, Roles } from '@shared/types'
import { ConfirmationDialog } from '@shared/components/confirmation.dialog.component'
import { useResourceRemove } from '@shared/hooks/use-resource-remove.hook'
import { AspectsDialog } from '@modules/aspects/aspects.dialog.component'
import { Table } from '@shared/components/table/table.component'
import { originalIcons } from '@shared/components/icons/icons'
import { isArray } from 'lodash'
import { MaterialTableProps } from '@material-table/core'
import { useACL } from '@shared/hooks/use-acl.hook'

export const aspectsEndpoint = `Aspects`

export function AspectsPage() {
  const { is } = useACL()
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const isModifyAllowed = is([
    Roles.Admin,
    Roles.Auditor,
    Roles.ClientAdministrator,
    Roles.ClientEditor,
  ])
  const {
    query: { companyID },
    navigate,
    location,
  } = useRouter<{ companyID: string }>()

  const {
    isRemovalDialogOpened,
    isResourceRemoving,
    setRemovalDialogOpened,
    setRemovedResourceID,
    onResourceRemove,
    isInvalidatingAfterRemove,
    isResourceRemoveSucceed,
  } = useResourceRemove(`companies/${companyID}/Aspects`)

  const {
    data: aspects,
    isLoading: areAspectsLoading,
    refetch,
  } = useQuery<Aspect[]>(aspectsEndpoint, () =>
    apiJSON(`companies/${companyID}/Aspects`)
  )

  const handleReloadAfterCreate = () => {
    refetch()
    setIsAddDialogOpen(false)
  }

  useEffect(() => {
    if (isResourceRemoveSucceed) refetch()
  }, [isResourceRemoveSucceed])

  return (
    <>
      {isAddDialogOpen ? (
        <AspectsDialog
          companyID={companyID}
          onClose={() => setIsAddDialogOpen(false)}
          afterCreate={handleReloadAfterCreate}
        />
      ) : null}
      <Table<Aspect>
        isLoading={areAspectsLoading || isInvalidatingAfterRemove}
        title="Aspekty środowiskowe"
        columns={[
          {
            title: 'Nazwa',
            field: 'name',
            sorting: true,
            defaultSort: 'asc',
            customSort: (a, b) =>
              a.name.localeCompare(b.name, 'pl', { sensitivity: 'base' }),
          },
          {
            title: 'Znaczący',
            field: 'isSignificant',
            render: ({ isSignificant }) =>
              isSignificant ? (
                <Checkbox checked={isSignificant} style={{ marginLeft: -10 }} />
              ) : null,
          },
        ]}
        data={aspects ?? []}
        options={{
          pageSize: 20,
        }}
        actions={
          isModifyAllowed
            ? ([
                {
                  icon: originalIcons.Create,
                  tooltip: 'Edytuj',
                  onClick: (
                    event: React.MouseEvent<HTMLElement>,
                    resourceEntity
                  ) => {
                    if (!isArray(resourceEntity)) {
                      navigate(
                        `/aspects/${companyID}/edit/${resourceEntity.id}`,
                        {
                          state: {
                            backgroundLocation: location,
                          },
                        }
                      )
                    }
                  },
                },
                {
                  icon: originalIcons.Delete,
                  tooltip: 'Usuń',
                  onClick: (
                    event: React.MouseEvent<HTMLElement>,
                    resourceEntity
                  ) => {
                    if (!isArray(resourceEntity)) {
                      setRemovedResourceID(resourceEntity.id)
                      setRemovalDialogOpened(true)
                    }
                  },
                },
                {
                  icon: originalIcons.Add,
                  isFreeAction: true,
                  onClick: () => setIsAddDialogOpen(true),
                },
              ] as MaterialTableProps<any>['actions'])
            : []
        }
      />
      <ConfirmationDialog
        submitButtonProps={{
          children: 'Usuń',
          isLoading: isResourceRemoving,
        }}
        title="Potwierdzenie usunięcia"
        onClose={onResourceRemove}
        open={isRemovalDialogOpened}
      >
        Czy napewno chcesz usunąć dany wiersz?
      </ConfirmationDialog>
      <Button
        variant="contained"
        onClick={() => navigate(-1)}
        style={{ backgroundColor: '#C0C0C0' }}
      >
        Powrót
      </Button>
    </>
  )
}

export const useStyles = makeStyles((theme) => {
  return {
    actionText: {
      textDecoration: 'underline',
      textAlign: 'right',
      cursor: 'pointer',
      display: 'inline-block',
      marginRight: theme.spacing(2),
    },
  }
})
